import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileUp, Loader2, CheckCircle2, AlertTriangle, Sparkles, Link2, Bot, Plus, X, Check, Split } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { DatePickerHR } from "../components/DatePickerHR";
import api, { fmtNum, fmtDate } from "../lib/api";

const MatchBadge = ({ r, i, onOpen }) => {
  if (r.duplicate) {
    return (
      <Badge data-testid={`row-duplicate-${i}`} className="bg-[#DC2626]/10 text-[#DC2626] hover:bg-[#DC2626]/10 rounded-sm text-xs gap-1 w-fit">
        <AlertTriangle className="w-3 h-3" /> Već uneseno
      </Badge>
    );
  }
  if (r.match?.kind === "strict" || r.match?.kind === "manual") {
    return (
      <button data-testid={`row-match-plan-${i}`} onClick={onOpen} className="text-left">
        <Badge className="bg-[#0D9488]/10 text-[#0D9488] hover:bg-[#0D9488]/20 rounded-sm text-xs gap-1 w-fit cursor-pointer">
          <Link2 className="w-3 h-3" /> {r.match.kind === "manual" ? "Upareno (potvrđeno)" : "Uparuje se s planom"}
        </Badge>
      </button>
    );
  }
  if (r.match?.kind === "ai") {
    return (
      <button data-testid={`row-match-ai-${i}`} onClick={onOpen} className="text-left">
        <Badge className="bg-[#D97706]/15 text-[#D97706] hover:bg-[#D97706]/25 rounded-sm text-xs gap-1 w-fit cursor-pointer">
          <Bot className="w-3 h-3" /> AI prijedlog — potvrdite
        </Badge>
      </button>
    );
  }
  return (
    <button data-testid={`row-match-new-${i}`} onClick={onOpen} className="text-left">
      <Badge className="bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0] rounded-sm text-xs gap-1 w-fit cursor-pointer">
        <Plus className="w-3 h-3" /> Novo
      </Badge>
    </button>
  );
};

const MatchPopover = ({ r, i, plans, onConfirm, onReject, onPickPlan, onSplit, children }) => {
  const candidates = plans.filter((p) => p.type === r.type && p.currency === r.currency);
  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent data-testid={`match-popover-${i}`} className="w-96 rounded-sm p-4 space-y-3" align="start">
        <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B]">Uparivanje s planom</p>
        {r.match ? (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="border border-[#E2E8F0] rounded-sm p-2">
                <p className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1">Iz izvoda</p>
                <p className="font-medium text-[#0F172A] truncate">{r.counterparty || "—"}</p>
                <p className="tabular-nums">{fmtNum(r.amount, r.currency)}</p>
                <p className="text-xs text-[#475569]">{fmtDate(r.date)}</p>
              </div>
              <div className="border border-[#0D9488]/30 bg-[#0D9488]/5 rounded-sm p-2">
                <p className="text-[10px] uppercase tracking-wider text-[#0D9488] mb-1">Planirano</p>
                <p className="font-medium text-[#0F172A] truncate">{r.match.plan.counterparty || r.match.plan.category}</p>
                <p className="tabular-nums">{fmtNum(r.match.plan.amount, r.match.plan.currency)}</p>
                <p className="text-xs text-[#475569]">{fmtDate(r.match.plan.date)}</p>
              </div>
            </div>
            {r.match.reason && <p className="text-xs text-[#475569] italic">{r.match.reason}</p>}
            <div className="flex gap-2">
              {r.match.kind === "ai" && (
                <Button data-testid={`match-confirm-${i}`} size="sm" onClick={() => onConfirm(i)} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm h-8 flex-1">
                  <Check className="w-3.5 h-3.5 mr-1" /> Potvrdi uparivanje
                </Button>
              )}
              <Button data-testid={`match-reject-${i}`} size="sm" variant="outline" onClick={() => onReject(i)} className="rounded-sm h-8 flex-1 border-[#CBD5E1] text-[#DC2626]">
                <X className="w-3.5 h-3.5 mr-1" /> Ne uparuj (novo)
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#475569]">Uvest će se kao nova ostvarena transakcija bez uparivanja.</p>
        )}
        <div className="space-y-1.5">
          <p className="text-xs text-[#475569]">Upari s drugim planom:</p>
          <Select onValueChange={(pid) => onPickPlan(i, pid)}>
            <SelectTrigger data-testid={`match-plan-select-${i}`} className="h-8 text-sm rounded-sm">
              <SelectValue placeholder={candidates.length ? "Odaberi plan..." : "Nema otvorenih planova (isti tip/valuta)"} />
            </SelectTrigger>
            <SelectContent>
              {candidates.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {fmtDate(p.date)} · {(p.counterparty || p.category).slice(0, 28)} · {fmtNum(p.amount, p.currency)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {candidates.length >= 2 && (
          <Button data-testid={`match-split-btn-${i}`} size="sm" variant="outline" onClick={() => onSplit(i)} className="w-full rounded-sm h-8 border-[#0D9488]/40 text-[#0D9488]">
            <Split className="w-3.5 h-3.5 mr-1.5" /> Rasporedi uplatu na više računa
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
};

const parseAmountInput = (s) => {
  const v = String(s ?? "").trim();
  if (!v) return NaN;
  return parseFloat(v.includes(",") ? v.replace(/\./g, "").replace(",", ".") : v);
};

export default function ImportPage() {
  const [banks, setBanks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bankId, setBankId] = useState("");
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [rows, setRows] = useState(null);
  const [plans, setPlans] = useState([]);
  const [fileName, setFileName] = useState("");
  const [stmt, setStmt] = useState(null);
  const [stmtBalance, setStmtBalance] = useState("");
  const [controlResult, setControlResult] = useState(null);
  const [splitRow, setSplitRow] = useState(null);
  const [splitSel, setSplitSel] = useState([]);
  const fileRef = useRef();
  const banksRef = useRef([]);

  useEffect(() => {
    api.get("/banks").then((r) => { setBanks(r.data); banksRef.current = r.data; if (r.data[0]) setBankId(r.data[0].id); });
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const parseFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setParsing(true);
    setRows(null);
    setControlResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/statements/parse", fd, { timeout: 180000 });
      setRows(r.data.transactions.map((t) => ({ ...t, include: !t.duplicate, no_match: false })));
      setPlans(r.data.plans || []);
      const st = r.data.statement || null;
      setStmt(st);
      setStmtBalance(st?.end_balance != null ? String(st.end_balance).replace(".", ",") : "");
      const parserBankShort = { asa: "ASA", procredit: "ProCredit" }[r.data.parser];
      if (parserBankShort) {
        const pb = banksRef.current.find((b) => (b.short || "").toLowerCase() === parserBankShort.toLowerCase());
        if (pb) {
          setBankId((cur) => {
            if (cur !== pb.id) toast.info(`Banka automatski postavljena na ${pb.short} prema formatu izvoda`);
            return pb.id;
          });
        }
      }
      if (st?.complete === false) {
        toast.warning("Zbroj stavki ne odgovara stanju na izvodu — možda nisu sve stavke pročitane! Usporedite broj redaka s izvodom.", { duration: 10000 });
      }
      const parserLabel = { asa: "Prepoznat ASA format", procredit: "Prepoznat ProCredit format", ai: "AI parsiranje" }[r.data.parser] || "";
      toast.success(`Pronađeno ${r.data.count} transakcija (${parserLabel})`);
      if (r.data.duplicates > 0) {
        toast.warning(`${r.data.duplicates} već unesenih transakcija — zaključane i neće se uvesti ponovno`);
      }
      const aiCount = r.data.transactions.filter((t) => t.match?.kind === "ai").length;
      if (aiCount > 0) toast.info(`${aiCount} AI ${aiCount === 1 ? "prijedlog uparivanja čeka" : "prijedloga uparivanja čeka"} vašu potvrdu (žuta oznaka)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri obradi izvoda");
    } finally {
      setParsing(false);
    }
  };

  const setRow = (i, k, v) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const confirmMatch = (i) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, no_match: false, match: { ...r.match, kind: "manual" } } : r)));
  const rejectMatch = (i) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, match: null, no_match: true } : r)));
  const pickPlan = (i, pid) => {
    const p = plans.find((x) => x.id === pid);
    if (!p) return;
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, no_match: false, match: { kind: "manual", plan: p, reason: "Ručno odabrano" } } : r)));
  };

  const openSplit = (i) => {
    setSplitSel(rows[i].plan_ids || []);
    setSplitRow(i);
  };
  const toggleSplitPlan = (pid) => setSplitSel((sel) => (sel.includes(pid) ? sel.filter((x) => x !== pid) : [...sel, pid]));
  const confirmSplit = () => {
    setRows((rs) => rs.map((r, idx) => (idx === splitRow ? { ...r, plan_ids: [...splitSel], match: null, no_match: true } : r)));
    toast.success(`Uplata će se rasporediti na ${splitSel.length} planiranih računa`);
    setSplitRow(null);
  };
  const clearSplit = () => {
    setRows((rs) => rs.map((r, idx) => (idx === splitRow ? { ...r, plan_ids: null, no_match: false } : r)));
    setSplitRow(null);
  };

  const included = rows?.filter((r) => r.include && !r.duplicate).length || 0;
  const selectableCount = rows?.filter((r) => !r.duplicate).length || 0;
  const pendingAi = rows?.filter((r) => r.include && !r.duplicate && r.match?.kind === "ai").length || 0;

  const doImport = async () => {
    const selected = rows.filter((r) => r.include && !r.duplicate);
    if (!selected.length) return toast.error("Odaberite barem jednu transakciju");
    setImporting(true);
    try {
      const balVal = parseAmountInput(stmtBalance);
      const curs = rows.map((r) => r.currency);
      const stmtCur = stmt?.currency || (curs.length ? curs.sort((a, b) => curs.filter((c) => c === a).length - curs.filter((c) => c === b).length).pop() : "BAM");
      const r = await api.post("/transactions/bulk", {
        bank_id: bankId,
        filename: fileName,
        statement_balance: isNaN(balVal) ? null : balVal,
        statement_currency: stmtCur,
        statement_date: stmt?.date_to || "",
        transactions: selected.map(({ include, duplicate, dup_in_file, learned, match, no_match, plan_ids, ...t }) => ({
          ...t,
          bank_id: bankId,
          amount: parseFloat(String(t.amount).replace(",", ".")),
          plan_id: plan_ids?.length ? null : (match && (match.kind === "strict" || match.kind === "manual") ? match.plan.id : null),
          plan_ids: plan_ids?.length ? plan_ids : null,
          no_match: plan_ids?.length ? true : (no_match || match?.kind === "ai"),
          dup_suspect: duplicate === true,
          force_duplicate: duplicate === true,
        })),
      });
      let msg = `Uvezeno ${r.data.imported} transakcija`;
      if (r.data.realized) msg += `, ostvareno ${r.data.realized} planiranih`;
      if (r.data.skipped) msg += `, preskočeno ${r.data.skipped} duplikata`;
      toast.success(msg);
      setControlResult(r.data.control || null);
      if (r.data.control && !r.data.control.matched) {
        toast.error(`Stanje se NE slaže s izvodom — razlika ${r.data.control.difference} ${r.data.control.currency}`, { duration: 10000 });
      }
      setRows(null);
      setFileName("");
      setStmt(null);
      setStmtBalance("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri uvozu");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="import-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">AI uvoz</p>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Uvoz izvoda</h1>
        <p className="text-sm text-[#475569] mt-2">Učitajte PDF ili CSV bankovnog izvoda — AI automatski izvlači sve transakcije, uparuje ih s planovima i sve pregledate prije uvoza.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <div
            data-testid="upload-dropzone"
            onClick={() => !parsing && fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); parseFile(e.dataTransfer.files[0]); }}
            className="border-2 border-dashed border-[#CBD5E1] hover:border-[#0D9488] rounded-sm bg-white p-10 text-center cursor-pointer transition-colors duration-200"
          >
            <input ref={fileRef} data-testid="file-input" type="file" accept=".pdf,.csv,.txt" className="hidden" onChange={(e) => parseFile(e.target.files[0])} />
            {parsing ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 text-[#0D9488] animate-spin" />
                <p className="text-sm font-medium text-[#0F172A]">AI obrađuje izvod "{fileName}"...</p>
                <p className="text-xs text-[#64748B]">Ovo može potrajati do minute</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <FileUp className="w-8 h-8 text-[#64748B]" />
                <p className="text-sm font-medium text-[#0F172A]">Kliknite ili povucite izvod ovdje</p>
                <p className="text-xs text-[#64748B]">PDF, CSV ili TXT · max 15MB</p>
                <p className="text-xs text-[#0D9488]">ASA i ProCredit CSV izvodi prepoznaju se automatski i točno, ostali formati preko AI-a</p>
                <p className="text-xs text-[#64748B]">Već unesene transakcije automatski se preskaču — isti izvod se ne može uvesti dvaput</p>
              </div>
            )}
          </div>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-5 space-y-3">
          <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B]">Račun banke</p>
          <Select value={bankId} onValueChange={setBankId}>
            <SelectTrigger data-testid="import-bank-select"><SelectValue placeholder="Odaberi banku" /></SelectTrigger>
            <SelectContent>
              {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-[#475569]">Sve transakcije iz izvoda knjižit će se na ovaj račun.</p>
        </div>
      </div>

      {controlResult && (
        <div data-testid="import-control-result" className={`flex items-start gap-3 border-2 rounded-sm px-4 py-4 ${controlResult.matched ? "bg-[#2F7D4F]/5 border-[#2F7D4F]/40" : "bg-[#DC2626]/5 border-[#DC2626]/40"}`}>
          {controlResult.matched ? <CheckCircle2 className="w-5 h-5 text-[#2F7D4F] shrink-0 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-[#DC2626] shrink-0 mt-0.5" />}
          <div className="text-sm">
            <p className="font-semibold text-[#0F172A]">
              Kontrola stanja izvoda ({controlResult.currency}, {fmtDate(controlResult.date)}): {controlResult.matched ? "stanje se slaže ✓" : `RAZLIKA ${controlResult.difference > 0 ? "+" : ""}${fmtNum(controlResult.difference)} ${controlResult.currency}`}
            </p>
            <p className="text-xs text-[#475569] mt-1">
              Izvod: {fmtNum(controlResult.statement_balance)} · Aplikacija: {fmtNum(controlResult.app_balance)} — rezultat je spremljen kao kontrolna točka.
              {!controlResult.matched && <> Otvorite <Link to="/uskladivanje" className="text-[#0D9488] font-semibold underline">Usklađivanje</Link> za dijagnostiku razlike.</>}
            </p>
          </div>
        </div>
      )}

      {rows && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-heading text-xl font-semibold text-[#0F172A]">Pregled prije uvoza — {included}/{rows.length} odabrano</h2>
            <Button data-testid="import-selected-btn" onClick={doImport} disabled={importing || !included} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Uvezi odabrano ({included})
            </Button>
          </div>
          <div data-testid="statement-control-card" className="bg-white border border-[#E2E8F0] rounded-sm px-4 py-3 flex flex-wrap items-end gap-4">
            <div className="text-sm">
              <p className="font-semibold text-[#0F172A]">Kontrola stanja izvoda</p>
              <p className="text-xs text-[#475569] mt-0.5">
                Izvod je referentna točka — sve stavke moraju biti uvezene.
                {stmt?.prev_balance != null && <> Prethodno stanje: <strong className="tabular-nums">{fmtNum(stmt.prev_balance)} {stmt.currency}</strong>.</>}
                {stmt?.date_from && <> Razdoblje: {fmtDate(stmt.date_from)} – {fmtDate(stmt.date_to)}</>}
              </p>
              {stmt?.complete === false && (
                <p data-testid="statement-incomplete-warning" className="text-xs font-semibold text-[#DC2626] mt-1">⚠ Zbroj stavki ne odgovara stanju izvoda — provjerite jesu li sve stavke pročitane!</p>
              )}
              {stmt?.complete === true && (
                <p data-testid="statement-complete-ok" className="text-xs font-semibold text-[#2F7D4F] mt-1">✓ Zbroj stavki odgovara stanju izvoda — sve stavke su pročitane.</p>
              )}
            </div>
            <div className="space-y-1 ml-auto">
              <p className="text-xs text-[#475569]">Novo stanje na izvodu ({stmt?.currency || "BAM"})</p>
              <Input data-testid="statement-balance-input" className="w-44 h-9" placeholder="npr. 12.345,67" value={stmtBalance} onChange={(e) => setStmtBalance(e.target.value)} />
              <p className="text-[10px] text-[#64748B]">Nakon uvoza automatska usporedba sa saldom aplikacije</p>
            </div>
          </div>
          {pendingAi > 0 && (
            <div data-testid="ai-pending-banner" className="flex items-start gap-2 bg-[#D97706]/10 border border-[#D97706]/30 rounded-sm px-4 py-3 text-sm text-[#0F172A]">
              <Bot className="w-4 h-4 text-[#D97706] shrink-0 mt-0.5" />
              <p><span className="font-semibold">{pendingAi}</span> AI {pendingAi === 1 ? "prijedlog uparivanja nije potvrđen" : "prijedloga uparivanja nije potvrđeno"} — kliknite na žutu oznaku i potvrdite ili odbijte. Nepotvrđeni prijedlozi uvest će se kao nove transakcije (plan ostaje otvoren).</p>
            </div>
          )}
          <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F1F5F9] hover:bg-[#F1F5F9]">
                  <TableHead className="w-10">
                    <Checkbox data-testid="select-all-checkbox" checked={selectableCount > 0 && included === selectableCount} onCheckedChange={(v) => setRows((rs) => rs.map((r) => ({ ...r, include: r.duplicate ? false : !!v })))} />
                  </TableHead>
                  <TableHead className="w-8 text-center">#</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead className="w-44">Uparivanje</TableHead>
                  <TableHead>Kome / od koga</TableHead>
                  <TableHead>Iznos</TableHead>
                  <TableHead>Valuta</TableHead>
                  <TableHead>Tip</TableHead>
                  <TableHead>Kategorija</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="parsed-rows-body">
                {rows.map((r, i) => (
                  <TableRow key={i} className={`transition-colors duration-200 ${r.include && !r.duplicate ? "" : "opacity-40"}`}>
                    <TableCell><Checkbox data-testid={`row-include-${i}`} disabled={r.duplicate} checked={r.include && !r.duplicate} onCheckedChange={(v) => setRow(i, "include", !!v)} /></TableCell>
                    <TableCell data-testid={`row-num-${i}`} className="text-center text-xs text-[#64748B] tabular-nums font-medium">{i + 1}</TableCell>
                    <TableCell><DatePickerHR data-testid={`row-date-${i}`} value={r.date} onChange={(v) => v && setRow(i, "date", v)} className="h-8 w-40 text-xs px-2" /></TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {r.duplicate ? (
                          <MatchBadge r={r} i={i} onOpen={() => {}} />
                        ) : r.plan_ids?.length ? (
                          <button data-testid={`row-split-badge-${i}`} onClick={() => openSplit(i)} className="text-left">
                            <Badge className="bg-[#2F7D4F]/15 text-[#2F7D4F] hover:bg-[#2F7D4F]/25 rounded-sm text-xs gap-1 w-fit cursor-pointer">
                              <Split className="w-3 h-3" /> Raspoređeno na {r.plan_ids.length} računa
                            </Badge>
                          </button>
                        ) : (
                          <MatchPopover r={r} i={i} plans={plans} onConfirm={confirmMatch} onReject={rejectMatch} onPickPlan={pickPlan} onSplit={openSplit}>
                            <div><MatchBadge r={r} i={i} onOpen={() => {}} /></div>
                          </MatchPopover>
                        )}
                        {r.match && !r.duplicate && (
                          <p className="text-[11px] text-[#475569] truncate max-w-[170px]" title={r.match.reason}>
                            {(r.match.plan.counterparty || r.match.plan.category).slice(0, 22)} · {fmtNum(r.match.plan.amount, r.match.plan.currency)}
                          </p>
                        )}
                        {r.dup_in_file && !r.duplicate && (
                          <Badge data-testid={`row-dupfile-${i}`} title="Više redaka s istim iznosom u ovoj datoteci (npr. bankarske naknade) — to je normalno, svi se uvoze" className="bg-[#F1F5F9] text-[#475569] hover:bg-[#F1F5F9] rounded-sm text-[10px] gap-1 w-fit">
                            Isti iznos u datoteci — uvozi se
                          </Badge>
                        )}
                        {r.learned && (
                          <Badge data-testid={`row-learned-${i}`} title="Kategorija predložena iz prošlih unosa za ovog primatelja" className="bg-[#0D9488]/10 text-[#0D9488] hover:bg-[#0D9488]/10 rounded-sm text-[10px] gap-1 w-fit">
                            <Sparkles className="w-3 h-3" /> Naučeno
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Input data-testid={`row-counterparty-${i}`} className="h-8 min-w-[160px] text-sm" value={r.counterparty} onChange={(e) => setRow(i, "counterparty", e.target.value)} />
                      {(r.recipient_bank || r.recipient_account) && (
                        <p data-testid={`row-recipient-info-${i}`} className="text-[10px] text-[#64748B] mt-1 truncate max-w-[200px]" title={`${r.recipient_bank || ""} ${r.recipient_account || ""}`}>
                          {r.recipient_bank}{r.recipient_bank && r.recipient_account ? " · " : ""}{r.recipient_account}
                        </p>
                      )}
                    </TableCell>
                    <TableCell><Input data-testid={`row-amount-${i}`} type="number" step="0.01" className="h-8 w-28 text-sm tabular-nums text-right" value={r.amount} onChange={(e) => setRow(i, "amount", e.target.value)} /></TableCell>
                    <TableCell>
                      <Select value={r.currency} onValueChange={(v) => setRow(i, "currency", v)}>
                        <SelectTrigger className="h-8 w-24 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="EUR">EUR</SelectItem><SelectItem value="BAM">BAM</SelectItem></SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select value={r.type} onValueChange={(v) => setRow(i, "type", v)}>
                        <SelectTrigger className="h-8 w-32 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="placanje">Plaćanje</SelectItem><SelectItem value="naplata">Naplata</SelectItem></SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Select value={r.category} onValueChange={(v) => setRow(i, "category", v)}>
                          <SelectTrigger className="h-8 w-40 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {r.category_ai && (
                          <span
                            data-testid={`row-ai-category-${i}`}
                            title="Kategoriju predložila umjetna inteligencija (Jev) — provjerite i po potrebi promijenite"
                            className="text-[9px] font-bold px-1 py-0.5 rounded-sm bg-[#D97706]/15 text-[#8A6420] shrink-0 cursor-help"
                          >
                            AI
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {splitRow != null && rows && (() => {
        const splitR = rows[splitRow];
        const splitAmount = parseFloat(String(splitR.amount).replace(",", ".")) || 0;
        const usedElsewhere = new Set();
        rows.forEach((r, idx) => {
          if (idx === splitRow) return;
          (r.plan_ids || []).forEach((id) => usedElsewhere.add(id));
          if (r.match && (r.match.kind === "strict" || r.match.kind === "manual")) usedElsewhere.add(r.match.plan.id);
        });
        const cands = plans.filter((p) => p.type === splitR.type && p.currency === splitR.currency && !usedElsewhere.has(p.id));
        const selPlans = splitSel.map((id) => cands.find((p) => p.id === id)).filter(Boolean);
        const selSum = +selPlans.reduce((s, p) => s + p.amount, 0).toFixed(2);
        const diff = +(selSum - splitAmount).toFixed(2);
        const last = selPlans[selPlans.length - 1];
        const overAllocated = last && selSum - last.amount >= splitAmount - 0.01;
        const canConfirm = selPlans.length >= 1 && diff >= -0.01 && !overAllocated;
        const cur = splitR.currency === "EUR" ? "€" : "KM";
        return (
          <Dialog open onOpenChange={(o) => !o && setSplitRow(null)}>
            <DialogContent data-testid="split-dialog" className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2"><Split className="w-4 h-4 text-[#0D9488]" /> Rasporedi uplatu na više računa</DialogTitle>
              </DialogHeader>
              <div className="bg-[#0D9488]/5 border border-[#0D9488]/25 rounded-sm px-3 py-2 text-sm" data-testid="split-payment-info">
                <span className="font-semibold text-[#0F172A]">{splitR.type === "naplata" ? "Uplata" : "Isplata"} iz izvoda:</span>{" "}
                {fmtDate(splitR.date)} · {splitR.counterparty || "—"} · <span className="font-bold tabular-nums text-[#0D9488]">{fmtNum(splitAmount, cur)}</span>
              </div>
              <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="split-plan-list">
                {cands.length === 0 && <p className="text-sm text-[#64748B]">Nema otvorenih planova istog tipa i valute.</p>}
                {cands.map((p) => {
                  const selIdx = splitSel.indexOf(p.id);
                  return (
                    <div key={p.id} data-testid={`split-plan-${p.id}`} onClick={() => toggleSplitPlan(p.id)} className={`flex items-center gap-3 border rounded-sm px-3 py-2 cursor-pointer transition-colors duration-150 ${selIdx >= 0 ? "border-[#0D9488]/50 bg-[#0D9488]/5" : "border-[#E2E8F0] hover:bg-[#F8FAFC]"}`}>
                      <Checkbox checked={selIdx >= 0} className="pointer-events-none" tabIndex={-1} />
                      {selIdx >= 0 && <span className="w-5 h-5 rounded-sm bg-[#0D9488] text-white text-[11px] font-bold flex items-center justify-center shrink-0">{selIdx + 1}</span>}
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-[#0F172A] truncate">{p.counterparty || p.category}</span>
                        <span className="block text-xs text-[#64748B] truncate">{fmtDate(p.date)}{p.note ? ` · ${p.note}` : ""}</span>
                      </span>
                      <span className="font-semibold tabular-nums text-sm shrink-0">{fmtNum(p.amount, p.currency === "EUR" ? "€" : "KM")}</span>
                    </div>
                  );
                })}
              </div>
              <div data-testid="split-summary" className={`rounded-sm px-3 py-2.5 text-sm border ${!selPlans.length ? "bg-[#F1F5F9] border-[#E2E8F0] text-[#475569]" : overAllocated || diff < -0.01 ? "bg-[#DC2626]/10 border-[#DC2626]/40 text-[#7A3830]" : diff > 0.01 ? "bg-[#D97706]/10 border-[#D97706]/40 text-[#92400E]" : "bg-[#2F7D4F]/10 border-[#2F7D4F]/40 text-[#2F7D4F]"}`}>
                <p className="font-semibold tabular-nums">Uplata {fmtNum(splitAmount, cur)} · odabrano {selPlans.length} {selPlans.length === 1 ? "račun" : "računa"} = {fmtNum(selSum, cur)}</p>
                {!selPlans.length ? (
                  <p className="text-xs mt-0.5">Odaberite račune redom kojim se pokrivaju uplatom.</p>
                ) : diff < -0.01 ? (
                  <p className="text-xs mt-0.5">Nedostaje {fmtNum(-diff, cur)} — zbroj odabranih računa mora pokriti cijelu uplatu. Odaberite još računa.</p>
                ) : overAllocated ? (
                  <p className="text-xs mt-0.5">Previše odabrano — zadnji odabrani račun ne bi bio naplaćen ni djelomično. Maknite ga.</p>
                ) : diff > 0.01 ? (
                  <p className="text-xs mt-0.5">Zadnji odabrani ({last.counterparty || last.category}) bit će DJELOMIČNO naplaćen: {fmtNum(+(last.amount - diff).toFixed(2), cur)} od {fmtNum(last.amount, cur)} — ostaje otvoren plan {fmtNum(diff, cur)}.</p>
                ) : (
                  <p className="text-xs mt-0.5">Zbroj se točno poklapa — svi odabrani računi bit će naplaćeni u cijelosti. ✓</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                {rows[splitRow].plan_ids?.length > 0 && (
                  <Button data-testid="split-clear-btn" variant="outline" onClick={clearSplit} className="rounded-sm border-[#CBD5E1] text-[#DC2626] mr-auto">
                    <X className="w-4 h-4 mr-1" /> Poništi raspodjelu
                  </Button>
                )}
                <Button data-testid="split-cancel-btn" variant="outline" onClick={() => setSplitRow(null)} className="rounded-sm border-[#CBD5E1]">Odustani</Button>
                <Button data-testid="split-confirm-btn" disabled={!canConfirm} onClick={confirmSplit} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
                  <Check className="w-4 h-4 mr-1" /> Potvrdi raspodjelu ({selPlans.length})
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        );
      })()}
    </div>
  );
}
